import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../api/firebase";
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Snackbar,
  TextField,
  ButtonGroup,
} from "@mui/material";

// ✅ 再レンダリング防止のためにメモ化
const UserCard = React.memo(({ user, updateField }) => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar src={user.avatar} />
          <Box>
            <Typography variant="h6">
              {user.lastName} {user.firstName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {user.email}
            </Typography>
          </Box>
        </Box>

        <Box mt={2} display="flex" gap={2} flexWrap="wrap">
          <FormControlLabel
            control={
              <Switch
                checked={user.isMasterMind || false}
                onChange={() =>
                  updateField(user.id, "isMasterMind", user.isMasterMind)
                }
                color="success"
              />
            }
            label="MM権限"
          />
          <FormControlLabel
            control={
              <Switch
                checked={user.isVillager || false}
                onChange={() =>
                  updateField(user.id, "isVillager", user.isVillager)
                }
                color="primary"
              />
            }
            label="村民"
          />
          <FormControlLabel
            control={
              <Switch
                checked={user.isStop || false}
                onChange={() =>
                  updateField(user.id, "isStop", user.isStop)
                }
                color="secondary"
              />
            }
            label="停止"
          />
        </Box>

        <Typography variant="caption" color="textSecondary">
          登録日:{" "}
          {user.createdAt?.seconds
            ? new Date(user.createdAt.seconds * 1000).toLocaleDateString()
            : "-"}
        </Typography>
      </CardContent>
    </Card>
  );
});

const OperateUserList = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [searchableUsers, setSearchableUsers] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");
  const [userCounts, setUserCounts] = useState({ all: 0, villager: 0, mm: 0 });

  useEffect(() => {
    fetchInitialUsers();
    fetchUserCounts();
  }, []);

  const fetchInitialUsers = async () => {
    setLoading(true);
    const q = query(
      collection(db, "users"),
      orderBy("createdAt", "desc"),
      limit(100)
    );
    const snapshot = await getDocs(q);
    const docs = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
    setAllUsers(docs);
    setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
    setHasMore(snapshot.docs.length === 100);
    setLoading(false);
  };

  const fetchMoreUsers = async () => {
    if (!lastDoc || loading) return;
    setLoading(true);
    const q = query(
      collection(db, "users"),
      orderBy("createdAt", "desc"),
      startAfter(lastDoc),
      limit(100)
    );
    const snapshot = await getDocs(q);
    const docs = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
    setAllUsers((prev) => [...prev, ...docs]);
    setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
    setHasMore(snapshot.docs.length === 100);
    setLoading(false);
  };

  const fetchUserCounts = async () => {
    const snap = await getDocs(collection(db, "users"));
    const docs = snap.docs.map((doc) => doc.data());
    setUserCounts({
      all: docs.length,
      villager: docs.filter((u) => u.isVillager).length,
      mm: docs.filter((u) => u.isMasterMind).length,
    });
  };

  const fetchSearchableUsers = async () => {
    const snapshot = await getDocs(collection(db, "users"));
    const docs = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
    setSearchableUsers(docs);
  };

  useEffect(() => {
    if (searchTerm && searchableUsers.length === 0) {
      fetchSearchableUsers();
    }
  }, [searchTerm]);

  const updateField = async (userId, field, currentValue) => {
    const ref = doc(db, "users", userId);
    await updateDoc(ref, { [field]: !currentValue });

    const updateFn = (users) =>
      users.map((u) =>
        u.id === userId ? { ...u, [field]: !currentValue } : u
      );

    setAllUsers(updateFn);
    if (searchTerm) setSearchableUsers(updateFn);

    setSnackbarText(`「${field}」を更新しました`);
    setSnackbarOpen(true);
    fetchUserCounts();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const filteredUsers = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    const base = searchTerm ? searchableUsers : allUsers;

    return base.filter((user) => {
      const last = (user.lastName ?? "").toLowerCase();
      const first = (user.firstName ?? "").toLowerCase();
      const email = (user.email ?? "").toLowerCase();
      const matchSearch =
        last.includes(lower) || first.includes(lower) || email.includes(lower);
      const matchFilter =
        filterType === "all" ||
        (filterType === "villager" && user.isVillager) ||
        (filterType === "mm" && user.isMasterMind);
      return matchSearch && matchFilter;
    });
  }, [allUsers, searchableUsers, searchTerm, filterType]);

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        ユーザ管理（カード形式）
      </Typography>

      <TextField
        label="名前またはメールで検索"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />

      <ButtonGroup sx={{ mb: 3 }}>
        <Button
          variant={filterType === "all" ? "contained" : "outlined"}
          onClick={() => setFilterType("all")}
        >
          すべて（{userCounts.all}人）
        </Button>
        <Button
          variant={filterType === "villager" ? "contained" : "outlined"}
          onClick={() => setFilterType("villager")}
        >
          村民（{userCounts.villager}人）
        </Button>
        <Button
          variant={filterType === "mm" ? "contained" : "outlined"}
          onClick={() => setFilterType("mm")}
        >
          MM（{userCounts.mm}人）
        </Button>
      </ButtonGroup>

      {filteredUsers.map((user) => (
        <UserCard key={user.id} user={user} updateField={updateField} />
      ))}

      {loading && (
        <Box display="flex" justifyContent="center" my={2}>
          <CircularProgress />
        </Box>
      )}

      {!loading && hasMore && !searchTerm && (
        <Box display="flex" justifyContent="center">
          <Button variant="outlined" onClick={fetchMoreUsers}>
            もっと読む
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2500}
        onClose={handleSnackbarClose}
        message={snackbarText}
      />
    </Box>
  );
};

export default OperateUserList;
